import { useEffect, type ReactNode } from 'react';
import { t, type Leaf, type Locale } from '../../i18n/strings';
import { cx } from '../../lib/cx';
import styles from './aqua.module.css';

export interface AquaMenuItem {
  readonly id: string;
  readonly label: Leaf;
  /** Coche à gauche, pour les entrées qui basculent un réglage ou une fenêtre. */
  readonly checked?: boolean;
  readonly onSelect: () => void;
}

export interface AquaMenuDef {
  readonly id: string;
  readonly label: Leaf;
  readonly items: readonly AquaMenuItem[];
}

interface Props {
  readonly menus: readonly AquaMenuDef[];
  readonly open: string | null;
  readonly lang: Locale;
  readonly onOpen: (id: string | null) => void;
  /** Les menus du haut retombent, celui de « Démarrer » remonte. */
  readonly upward?: boolean;
}

/**
 * Les menus déroulants d'AquariumOS — Fichier, Nourrir, Fenêtres, Aide, et le
 * menu du bouton Démarrer.
 *
 * **Aucune entrée n'est décorative.** C'était le reproche : quatre libellés en
 * haut et un bouton Démarrer qui ne faisaient rien du tout. Chaque entrée agit
 * maintenant sur le bac, sur une fenêtre ou sur une boîte de dialogue — y
 * compris celles qui ne peuvent qu'échouer, comme « Nouveau poisson », qui
 * explique qu'il n'y en a qu'un et qu'il est déjà là.
 *
 * Comportement repris du skin xp, parce que c'est celui de tous les systèmes :
 * un clic ouvre, un second referme, survoler un autre titre pendant qu'un menu
 * est ouvert bascule dessus, `Échap` et un clic ailleurs referment.
 */
export function AquaMenu({ menus, open, lang, onOpen, upward }: Props): ReactNode {
  useEffect(() => {
    if (open === null) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onOpen(null);
    };
    // `pointerdown` et pas `click` : sinon le clic qui a ouvert le menu se
    // propage jusqu'ici et le referme dans la foulée.
    const onOutside = (event: PointerEvent): void => {
      const target = event.target;
      if (target instanceof Element && target.closest(`.${styles.menuHolder}`)) return;
      onOpen(null);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('pointerdown', onOutside);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onOutside);
    };
  }, [open, onOpen]);

  return (
    <>
      {menus.map((menu) => (
        <div key={menu.id} className={styles.menuHolder}>
          <button
            type="button"
            className={cx(styles.menuItem, open === menu.id && styles.menuItemOpen)}
            aria-haspopup="true"
            aria-expanded={open === menu.id}
            onClick={() => {
              onOpen(open === menu.id ? null : menu.id);
            }}
            onPointerEnter={() => {
              if (open !== null && open !== menu.id) onOpen(menu.id);
            }}
          >
            {t(menu.label, lang)}
          </button>

          {open === menu.id && (
            <div
              className={cx(styles.menuDrop, upward === true && styles.menuDropUp)}
              role="menu"
              aria-label={t(menu.label, lang)}
            >
              {menu.items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  role="menuitem"
                  className={styles.menuDropItem}
                  onClick={() => {
                    onOpen(null);
                    item.onSelect();
                  }}
                >
                  <span className={styles.menuCheck} aria-hidden="true">
                    {item.checked === true ? '◉' : ''}
                  </span>
                  <span>{t(item.label, lang)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      ))}
    </>
  );
}
